import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, ChevronLeft } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getSchema } from "@/components/admin/contentSchemas";
import { EntityForm } from "@/components/admin/EntityForm";

export default function AdminContent() {
    const { kind } = useParams();
    const schema = getSchema(kind);
    useSEO({ title: schema ? `${schema.label} — Admin | RKS Systems` : "Admin — RKS Systems" });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openForm, setOpenForm] = useState(false);
    const [editing, setEditing] = useState(null); // record being edited (null = create new)
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = () => {
        setLoading(true);
        api.get(`/admin/content/${kind}`).then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false));
    };

    useEffect(() => { if (schema) load(); /* eslint-disable-next-line */ }, [kind]);

    if (!schema) return <Navigate to="/admin/dashboard" replace />;

    const idOf = (row) => row[schema.idField] || row.id;

    const onCreate = () => {
        setEditing(null);
        setFormError("");
        setOpenForm(true);
    };

    const onEdit = async (row) => {
        setEditing(row);
        setFormError("");
        setOpenForm(true);
    };

    const onDelete = async (row) => {
        try {
            await api.delete(`/admin/content/${kind}/${idOf(row)}`);
            setConfirmDelete(null);
            load();
        } catch (e) {
            alert(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
        }
    };

    const onSubmit = async (values) => {
        setSubmitting(true);
        setFormError("");
        try {
            if (editing) {
                await api.put(`/admin/content/${kind}/${idOf(editing)}`, values);
            } else {
                await api.post(`/admin/content/${kind}`, values);
            }
            setOpenForm(false);
            load();
        } catch (e) {
            setFormError(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-8" data-testid={`page-admin-content-${kind}`}>
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/admin/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ChevronLeft className="h-3 w-3" /> Back to dashboard</Link>
                    <h1 className="mt-1 font-heading text-3xl font-bold">{schema.label}</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Create, edit and publish {schema.label.toLowerCase()}. Uploads are stored on the server and served under /api/uploads.</p>
                </div>
                <button onClick={onCreate} data-testid={`admin-create-${kind}`} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90">
                    <Plus className="h-4 w-4" /> New {schema.singular}
                </button>
            </div>

            <div className="mt-8 rounded-xl border border-border bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr>
                            {schema.columns.map((c) => (
                                <th key={c.key} className={`text-left px-4 py-3 font-heading font-semibold text-xs uppercase tracking-wider text-muted-foreground ${c.width || ""}`}>{c.label}</th>
                            ))}
                            <th className="text-right px-4 py-3 font-heading font-semibold text-xs uppercase tracking-wider text-muted-foreground w-32">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={schema.columns.length + 1} className="px-4 py-12 text-center text-muted-foreground"><Loader2 className="h-4 w-4 mx-auto animate-spin" /></td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={schema.columns.length + 1} className="px-4 py-12 text-center text-muted-foreground">No {schema.label.toLowerCase()} yet.</td></tr>
                        ) : items.map((row) => (
                            <tr key={idOf(row)} className="border-t border-border hover:bg-secondary/30" data-testid={`row-${idOf(row)}`}>
                                {schema.columns.map((c) => (
                                    <td key={c.key} className="px-4 py-3 align-top">{c.render ? c.render(row) : (row[c.key] ?? "—")}</td>
                                ))}
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => onEdit(row)} data-testid={`row-edit-${idOf(row)}`} className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-foreground/80 hover:bg-secondary"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                                    <button onClick={() => setConfirmDelete(row)} data-testid={`row-delete-${idOf(row)}`} className="ml-1 inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <EntityForm
                open={openForm}
                onClose={() => setOpenForm(false)}
                title={editing ? `Edit ${schema.singular}` : `New ${schema.singular}`}
                schema={schema.fields}
                initialValues={editing || {}}
                onSubmit={onSubmit}
                submitting={submitting}
                error={formError}
            />

            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="confirm-delete-overlay">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-border p-6">
                        <h3 className="font-heading text-xl font-bold">Delete this {schema.singular}?</h3>
                        <p className="mt-2 text-sm text-muted-foreground">This cannot be undone. Uploaded media is not deleted; manage it in the Media library.</p>
                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button onClick={() => setConfirmDelete(null)} data-testid="confirm-delete-cancel" className="rounded-md border border-input px-4 py-2 text-sm font-semibold hover:bg-secondary">Cancel</button>
                            <button onClick={() => onDelete(confirmDelete)} data-testid="confirm-delete-confirm" className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
